import { useState } from 'react';
import { colour, radius, space, text } from '../design/tokens.ts';
import { Button, Card, Field, Line, Page, Status, Tag, TextInput, Thumb } from '../components/basics.tsx';
import { CouldNotLoad, DoneToast, Loading, OfflineBanner } from '../components/states.tsx';
import { api } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';
import { useCan } from '../app/me.tsx';

/**
 * Screen 4: one take. The question Maestro exists to answer — what is the
 * latest known good, and where is it.
 *
 * `GET /api/takes/:id/detail` answers all of it in one request, so this screen
 * does not ask six times. The card is the newest result somebody **marked**
 * good, which is often not the newest that exists; the copy button copies
 * `arcfs_share\...` whatever this machine mounts it as.
 */

interface Location {
  uri: string | null;
  label: string;
  primary?: boolean;
  root: string;
}

interface GoodCard {
  artifact: { _id: string; kind: string; version: number; status: string };
  locations: Location[];
  copy: string;
  thumbnail: { url: string | null } | null;
  owner?: string | null;
}

interface Stage {
  kind: string;
  variant: string | null;
  missing: boolean;
  version?: number;
  status?: string;
  on?: string;
  final_ready?: boolean;
  says?: string;
}

interface Viewer {
  role: string;
  url: string;
  kind?: string;
  version?: number;
  fps?: number;
}

interface Note {
  _id?: string;
  text: string;
  author: string;
  created_at: string;
  at: { frame: number | null; timecode: string | null };
  about: { kind: string; version: number } | null;
}

interface TakeDetail {
  take: { _id: string; scene?: string; shot?: string; take?: string; conductor_take_id?: string };
  captures: unknown[];
  latest_good: Record<string, GoodCard>;
  stages: Stage[];
  viewers: { movie: Viewer[]; splat: Viewer[]; frames: Viewer[] };
  versions: Record<string, { _id: string; version: number; status: string; created_at: string }[]>;
  notes: Note[];
  next_stage: { kind: string; why: string } | null;
}

type Tab = 'movie' | 'splat' | 'frames';

/** The copy button. What it copies is what people say to each other (rule 8). */
function CopyRow({ copy }: { copy: string }) {
  const [said, setSaid] = useState(false);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space.tight }}>
      <code
        style={{
          padding: '6px 10px',
          borderRadius: radius.control,
          background: colour.surfaceSunk,
          border: `1px solid ${colour.edgeSoft}`,
          ...text.mono,
          wordBreak: 'break-all'
        }}
      >
        {copy}
      </code>
      <Button
        onClick={() => {
          void navigator.clipboard?.writeText(copy);
          setSaid(true);
          window.setTimeout(() => setSaid(false), 1800);
        }}
      >
        {said ? 'Copied' : 'Copy'}
      </Button>
    </div>
  );
}

export function TakeDetail({ takeId }: { takeId: string }) {
  const can = useCan();
  const [tab, setTab] = useState<Tab>('movie');
  const [toast, setToast] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [noteAt, setNoteAt] = useState('');

  const { state, reload } = useLoad<TakeDetail>(
    () => api.get(`/api/takes/${takeId}/detail`),
    [takeId]
  );
  const detail = state.data;

  const addNote = async (): Promise<void> => {
    const movie = detail?.latest_good.movie?.artifact._id;
    try {
      await api.post('/api/notes/at', {
        target_type: movie ? 'artifact' : 'take',
        target_id: movie ?? takeId,
        project_id: 'SAMPLE',
        text: noteText,
        ...(noteAt.includes(':') ? { timecode: noteAt } : noteAt ? { frame: Number(noteAt) } : {})
      });
      setNoteText('');
      setNoteAt('');
      setToast('Note added.');
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That note was not accepted.');
    }
  };

  if (state.status === 'loading' && !detail) {
    return (
      <Page eyebrow="MAESTRO · TAKE" title="Take">
        <Loading says="Loading this take…" rows={6} />
      </Page>
    );
  }
  if (!detail) {
    return (
      <Page eyebrow="MAESTRO · TAKE" title="Take">
        <CouldNotLoad what="this take" error={state.status === 'failed' ? state.error : null} onRetry={reload} />
      </Page>
    );
  }

  const name = [detail.take.scene, detail.take.shot, detail.take.take].filter(Boolean).join(' · ') || 'A take';
  const viewers = detail.viewers[tab] ?? [];

  return (
    <Page
      eyebrow="MAESTRO · TAKE"
      title={name}
      description={detail.take.conductor_take_id ? `Conductor ${detail.take.conductor_take_id}` : undefined}
      note={detail.next_stage ? `Next: ${detail.next_stage.kind} — ${detail.next_stage.why}` : undefined}
    >
      {state.status === 'offline' && <OfflineBanner since={state.since} onRetry={reload} />}

      {/* The latest known good, per kind */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))',
          gap: space.control
        }}
      >
        {Object.entries(detail.latest_good).map(([kind, card]) => (
          <Card key={kind} title={`Latest known good ${kind}`}>
            <div style={{ display: 'flex', gap: space.control, alignItems: 'flex-start' }}>
              <Thumb url={card.thumbnail?.url} alt={`${kind} thumbnail`} size={120} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Status value={card.artifact.status} detail={`version ${card.artifact.version}`} />
                {card.owner && <span style={{ ...text.note, color: colour.inkSoft }}>{card.owner}</span>}
              </div>
            </div>
            <CopyRow copy={card.copy} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {card.locations.map((where) => (
                <Tag key={`${where.root}-${where.label}`} tone="mono">
                  {where.root}
                  {where.primary ? ' · primary' : ''}
                </Tag>
              ))}
            </div>
          </Card>
        ))}
        {Object.keys(detail.latest_good).length === 0 && (
          <Card>
            <span style={{ ...text.body, color: colour.inkSoft }}>
              Nothing has been marked good for this take yet.
            </span>
          </Card>
        )}
      </div>

      {/* The stage strip */}
      <Card title="How far it has got" aside="Read off this project's pipeline.">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight }}>
          {detail.stages.map((stage) => (
            <div
              key={`${stage.kind}-${stage.variant ?? ''}`}
              style={{
                flex: '1 1 150px',
                minWidth: 150,
                padding: '10px 12px',
                borderRadius: radius.control,
                background: stage.missing ? colour.surfaceSunk : colour.goodWash,
                border: `1px solid ${stage.missing ? colour.edge : colour.good}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 4
              }}
            >
              <span style={text.smallStrong}>
                {stage.kind}
                {stage.variant ? ` · ${stage.variant}` : ''}
              </span>
              <span style={{ ...text.note, color: colour.inkSoft }}>
                {stage.missing ? 'not made yet' : `version ${stage.version} · ${stage.status}`}
              </span>
              {stage.on && (
                <span style={{ ...text.note, color: colour.inkSoft }}>on {stage.on}</span>
              )}
              {stage.final_ready && <Status value="stale" detail="a good final splat is waiting" />}
            </div>
          ))}
        </div>
      </Card>

      {/* Viewer tabs */}
      <Card
        title="Look at it"
        aside={
          <span style={{ display: 'flex', gap: 6 }}>
            {(['movie', 'splat', 'frames'] as Tab[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                aria-pressed={tab === key}
                style={{
                  minHeight: 36,
                  padding: '0 14px',
                  borderRadius: radius.pill,
                  border: `1px solid ${tab === key ? colour.accent : colour.edge}`,
                  background: tab === key ? colour.accentWash : colour.surface,
                  color: tab === key ? colour.accentInk : colour.ink,
                  font: text.smallStrong.font,
                  cursor: 'pointer',
                  textTransform: 'capitalize'
                }}
              >
                {key}
                {` (${detail.viewers[key]?.length ?? 0})`}
              </button>
            ))}
          </span>
        }
      >
        {viewers.length === 0 ? (
          <span style={{ ...text.small, color: colour.inkSoft }}>
            {tab === 'splat'
              ? 'The splat viewer is served by maestro_d from the machine the splat is on, and that machine is not reporting.'
              : 'Nothing to look at here yet.'}
          </span>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.control }}>
            {viewers.map((viewer) => (
              <div key={viewer.url} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {viewer.role === 'proxy_movie' ? (
                  <video
                    src={viewer.url}
                    controls
                    style={{ width: 'min(100%, 480px)', borderRadius: radius.control, background: colour.ink }}
                  />
                ) : viewer.role === 'splat_view' ? (
                  <a href={viewer.url} target="_blank" rel="noreferrer">
                    Open the splat viewer
                  </a>
                ) : (
                  <img
                    src={viewer.url}
                    alt={`${viewer.kind ?? ''} ${viewer.role}`}
                    style={{ maxWidth: 320, borderRadius: radius.control, border: `1px solid ${colour.edge}` }}
                  />
                )}
                <span style={{ ...text.note, color: colour.inkSoft }}>
                  {viewer.kind} {viewer.version ? `v${viewer.version}` : ''} · {viewer.role}
                  {viewer.fps ? ` · ${viewer.fps} fps` : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Versions */}
      <Card title="Every version" aside="Newest first. Nothing is ever deleted.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.control }}>
          {Object.entries(detail.versions).map(([kind, list]) => (
            <div key={kind} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space.tight }}>
              <span style={{ ...text.smallStrong, minWidth: 90 }}>{kind}</span>
              {list.map((version) => (
                <span key={version._id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Status value={version.status} detail={`v${version.version}`} />
                </span>
              ))}
            </div>
          ))}
        </div>
      </Card>

      {/* Notes */}
      <Card title="Notes" aside="One timeline for the take and everything it made.">
        {can.add_note && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: space.tight }}>
            <Field label="A note" grow>
              <TextInput value={noteText} onChange={setNoteText} placeholder="The wobble at the start is in the plate" />
            </Field>
            <Field label="At a frame or a timecode">
              <TextInput value={noteAt} onChange={setNoteAt} placeholder="36 or 01:00:04:12" />
            </Field>
            <Button tone="primary" onClick={() => void addNote()} disabled={noteText.trim() === ''}>
              Add
            </Button>
          </div>
        )}
        {detail.notes.length === 0 ? (
          <span style={{ ...text.small, color: colour.inkSoft }}>No notes yet.</span>
        ) : (
          detail.notes.map((note, i) => (
            <div
              key={note._id ?? i}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                paddingTop: space.tight,
                borderTop: i === 0 ? 'none' : `1px solid ${colour.edgeSoft}`
              }}
            >
              <span style={text.small}>{note.text}</span>
              <span style={{ ...text.note, color: colour.inkSoft }}>
                {note.author} · {new Date(note.created_at).toLocaleString()}
                {note.at.timecode ? ` · at ${note.at.timecode}` : ''}
                {note.at.frame != null ? ` (frame ${note.at.frame})` : ''}
                {note.about ? ` · on the ${note.about.kind} v${note.about.version}` : ''}
              </span>
            </div>
          ))
        )}
      </Card>

      <Line label="Where this came from">
        <a href={`/api/timeline/take/${takeId}`}>the full history</a>
      </Line>

      {toast && <DoneToast says={toast} onClose={() => setToast(null)} />}
    </Page>
  );
}
